import React, { useEffect, useState, useRef } from "react";
import { Sidebar } from "primereact/sidebar";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import ButtonsActions from "./ButtonsActions";
import { SelectButton } from "primereact/selectbutton";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { toastAlert, downloadFiles } from "../core/utils";
import { Toast } from "primereact/toast";
import { AssignmentService } from "../service/AssignmentService";
import { ProgressBar } from "primereact/progressbar";
import { Avatar } from 'primereact/avatar';
import { InputText } from 'primereact/inputtext';
import FlagEEUU from '../assets/images/flag-eeuu.png';
import FlgSpain from '../assets/images/flag-spain.png';
import { Image } from "primereact/image";
import UpdateDesignUpload from "../modules/assignments/UpdateDesignUpload";

function ExpandSidebar({ items, visible, hide, id, updateItems, updateButton }) {
    const service = useRef(new AssignmentService());
    const toast = useRef(null);
    
    const [currentItem, setCurrentItem] = useState(items[0] || {});
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [optionValue, setOptionValue] = useState(null);
    const [observation, setObservation] = useState(null);
    const [loadingButton, setLoadingButton] = useState(false);
    const [deleteDataDialog, setDeleteDataDialog] = useState(false);
    const [comment, setComment] = useState("");
    const [openModalUpload, setOpenModalUpload] = useState(false);

    const options = ["APPROVED", "DISAPPROVE"];

    useEffect(() => {
        if (items && items.length > 0) {
            setCurrentItem(items[0]);
        }
    }, [items]);

    const updateStatusInRealTime = (newStatus) => {
        setCurrentItem(prev => ({
            ...prev,
            _FILESTATUS: [...(prev._FILESTATUS || []), newStatus]
        }));
    };

    const statusTemplate = (status) => {
        if (!status) return null;
        
        const lastStatus = status[status.length - 1];
        let text, variant, icon;

        if (lastStatus === "SAVED") {
            text = "SAVED";
            variant = "info";
            icon = "pi pi-save";
        } else if (lastStatus === "APPROVED") {
            text = "APPROVED";
            variant = "success";
            icon = "pi pi-check-circle";
        } else {
            text = "DISAPPROVED";
            variant = "danger";
            icon = "pi pi-times";
        }

        return <Tag value={text} icon={icon} severity={variant} className="text-sm"></Tag>;
    };

    const dataSend = async () => {
        setLoadingButton(true);
        try {
            const data = {
                _ID: id,
                _IDFILE: currentItem._FILEID,
                _OPTION: optionValue,
                _OBSERVATION: observation,
            };

            updateStatusInRealTime(optionValue);

            const response = await service.current.approveDesign(data);
            
            if (response._SUCCESS) {
                toastAlert("success", response._MESSAGE, toast);
                updateItems(response._ITEMS);
            } else {
                toastAlert("warning", response._MESSAGE, toast);
        
                setCurrentItem(prev => ({
                    ...prev,
                    _FILESTATUS: prev._FILESTATUS.slice(0, -1)
                }));
            }
        } catch (error) {
            toastAlert("error", error.message, toast);
            setCurrentItem(prev => ({
                ...prev,
                _FILESTATUS: prev._FILESTATUS.slice(0, -1)
            }));
        } finally {
            setLoadingButton(false);
            setModalVisible(false);
        }
    };

    const addComments = async () => {
        setLoadingButton(true);
        try {
            const data = {
                _ID: id,
                _IDFILE: currentItem._FILEID,
                _TITLE: currentItem._FILENAME,
                _COMMENTS: comment,
            };

            const response = await service.current.addComments(data);
            
            if (response._SUCCESS) {
                const updatedItem = response._ITEMS._UPLOADED.find(
                    item => item._FILEID === currentItem._FILEID
                );
                setCurrentItem(updatedItem);
                toastAlert("success", response._MESSAGE, toast.current);
                setComment("");
            } else {
                toastAlert("warning", response._MESSAGE, toast.current);
            }
        } catch (error) {
            toastAlert("error", error.message, toast.current);
        } finally {
            setLoadingButton(false);
        }
    };

    const commentsTemplate = (comments) => {
        if (!comments || comments.length === 0) {
            return <Card><h5>No Comments</h5></Card>;
        }

        return comments.toReversed().map((comment, i) => (
            <Card key={i} className="mb-2">
                <div className="py-3 flex md:align-items-start flex-column md:flex-row">
                    <Avatar image={comment.user.avatar} size="large" />
                    <div className="ml-5">
                        <span className="text-900 font-medium block mb-2">{comment.user.name}</span>
                        <div className="text-700 mb-2">{comment.comment}</div>
                        <div className="text-primary">
                            <i className="pi pi-calendar text-sm mr-2"></i>
                            <span>{comment.date}</span>
                        </div>
                    </div>
                </div>
            </Card>
        ));
    };

    const footerComments = (
        <div className="mb-1" style={{ width: '40%' }}>
            <span className="p-input-icon-right" style={{ width: '100%' }}>
                <i 
                    className="pi pi-telegram cursor-pointer" 
                    onClick={addComments} 
                    style={{ fontSize: '1.5em', marginTop: '-10px' }} 
                />
                <InputTextarea 
                    className="border-1 border-round-top-xl border-round-bottom-xl border-solid w-full border-pink-600" 
                    rows={1} 
                    value={comment} 
                    placeholder="Add Comments"
                    onChange={(e) => setComment(e.target.value)} 
                />
            </span>
        </div>
    );

    return (
        <div>
            <Toast ref={toast} />
            <Sidebar visible={visible} fullScreen onHide={hide}>
                <div className={loading ? "flex justify-content-center align-items-center min-h-screen" : "hidden"}>
                    <h5>Loading...</h5>
                </div>
                
                <div className={loading ? "hidden" : "grid"}>
                    <div className="col-7">
                        <h2 className="text-color">Design Request</h2>
                        {currentItem && (
                            <Card>
                                <div className="flex justify-content-start align-items-center mb-2 mt-2">
                                    <h5>{currentItem._FILENAME}</h5>
                                </div>

                                <div className="flex justify-content-end align-items-center mb-2">
                                    <Button 
                                        tooltip="Approve" 
                                        tooltipOptions={{ position: "top" }} 
                                        className="p-button-success p-button-sm mr-1" 
                                        style={{ background: '#fff', color: '#000' }}
                                        onClick={() => setModalVisible(true)} 
                                    >
                                        <i className="pi pi-check" style={{ fontSize: "1.5em", color: 'green' }}></i>
                                        <span className="px-3">Approve</span>
                                    </Button>

                                    {currentItem._FILEURLENGLISH && (
                                        <Button 
                                            className="p-button-sm mr-1 p-1" 
                                            aria-label="English"
                                            tooltip="View-EN"
                                            tooltipOptions={{ position: "top" }}
                                            style={{ background: '#fff', color: '#000' }}
                                            onClick={() => downloadFiles(currentItem._FILEURLENGLISH, currentItem._FILENAME)}
                                        >
                                            <Image src={FlagEEUU} imageClassName="w-2rem" />
                                            <span className="px-3">View</span>
                                        </Button>
                                    )}

                                    {currentItem._FILEURLSPANISH && (
                                        <Button 
                                            className="p-button-sm mr-1 p-1" 
                                            aria-label="Spanish"
                                            tooltip="View-ESP"
                                            tooltipOptions={{ position: "top" }}
                                            style={{ background: '#fff', color: '#000' }}
                                            onClick={() => downloadFiles(currentItem._FILEURLSPANISH, currentItem._FILENAME)}
                                        >
                                            <Image src={FlgSpain} imageClassName="w-2rem" />
                                            <span className="px-3">View</span>
                                        </Button>
                                    )}

                                    <Button 
                                        tooltip="Update" 
                                        tooltipOptions={{ position: "top" }} 
                                        className="p-button-warning p-button-sm" 
                                        style={{ background: '#fff', color: '#000' }}
                                        onClick={() => setOpenModalUpload(true)} 
                                    >
                                        <i className="pi pi-replay" style={{ fontSize: "1.5em", color: 'red' }}></i>
                                        <span className="px-3">Update</span>
                                    </Button>
                                </div>

                                <ul className="list-none m-0 p-0">
                                    <li className="px-0 py-2 flex justify-content-between align-items-center border-bottom-1 surface-border">
                                        <span className="text-600 font-medium text-base">Format</span>
                                        <span className="text-900 font-medium text-base">{currentItem._FILEFORMAT}</span>
                                    </li>
                                    <li className="px-0 py-2 flex justify-content-between align-items-center border-bottom-1 surface-border">
                                        <span className="text-600 font-medium text-base">Created</span>
                                        <span className="text-900 font-medium text-base">{currentItem._FILEDATE}</span>
                                    </li>
                                    <li className="px-0 py-2 flex justify-content-between align-items-center border-bottom-1 surface-border">
                                        <span className="text-600 font-medium text-base">Status</span>
                                        <span className="text-900 font-medium text-base">
                                            {statusTemplate(currentItem._FILESTATUS)}
                                        </span>
                                    </li>
                                </ul>
                            </Card>
                        )}
                    </div>

                    <div className="col-5" style={{ height: '700px', overflowY: 'scroll', paddingBottom: '50px' }}>
                        <h2 className="text-color">Comments</h2>
                        {currentItem && commentsTemplate(currentItem._COMMENTS)}
                        
                        <div className="grid">
                            <div className="col-12 absolute z-2 bottom-0" style={{ paddingRight: '50px' }} >
                                {footerComments}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Diálogos modales */}
                <Dialog 
                    visible={openModalUpload} 
                    style={{ width: '550px' }} 
                    header={`Upload Design ${currentItem._FILEFORMAT}`}
                    modal
                    onHide={() => setOpenModalUpload(false)}
                >
                    <UpdateDesignUpload 
                        data={currentItem}
                        updateData={updateItems}
                        hide={() => setOpenModalUpload(false)}
                        uploadNotify={updateButton}
                        id={id}
                        closeExpand={hide}
                        onAlert={(event, message) => toastAlert(event, message, toast.current)}
                    />
                </Dialog>

                <Dialog 
                    header="Approve Design" 
                    visible={modalVisible} 
                    style={{ width: "40vw" }} 
                    modal
                    footer={
                        <ButtonsActions 
                            cancel={() => setModalVisible(false)} 
                            submit={dataSend} 
                            loading={loadingButton} 
                        />
                    }
                    onHide={() => setModalVisible(false)}
                >
                    <div className="p-fluid formgrid grid mt-2 mb-4">
                        <div className="field col-12 mb-5">
                            <SelectButton 
                                value={optionValue} 
                                options={options} 
                                onChange={(e) => setOptionValue(e.value)} 
                            />
                        </div>
                        <div className="field col-12">
                            <label htmlFor="observation" className="font-semibold">
                                Observation:
                            </label>
                            <InputTextarea 
                                rows={1} 
                                cols={2} 
                                value={observation || ""} 
                                onChange={(e) => setObservation(e.target.value)} 
                            />
                        </div>
                    </div>
                </Dialog>

                <Dialog 
                    visible={loadingButton} 
                    style={{ width: "450px" }} 
                    showHeader={false} 
                    modal 
                    onHide={() => {}}
                >
                    <div className="text-center">
                        <div className="grid">
                            <div className="col-12">
                                <p className="text-2xl p-4">
                                    <b>Please wait...</b>
                                </p>
                            </div>
                            <div className="col-12">
                                <ProgressBar mode="indeterminate" className="w-full text-color h-1rem"></ProgressBar>
                            </div>
                        </div>
                    </div>
                </Dialog>
            </Sidebar>
        </div>
    );
}

export default ExpandSidebar;